import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, BarChart, Bar, Cell
} from "recharts";
import db from "../Firebase";
import { useAuth } from "../AuthContext";
import { parseDaily, parseSources } from "../analytics";

const TEAL   = "#0AADA1";
const AMBER  = "#f59e0b";
const PURPLE = "#818cf8";

function getDays(n) {
    const days = [];
    for (let i = n - 1; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        days.push(d.toISOString().split("T")[0]);
    }
    return days;
}

function fmt(n = 0) {
    if (n >= 1000) return (n / 1000).toFixed(1) + "k";
    return String(n);
}

function shortDate(dateStr, range) {
    const d = new Date(dateStr + "T00:00:00");
    if (range === 7)  return d.toLocaleDateString("en", { weekday: "short" });
    return d.toLocaleDateString("en", { day: "numeric", month: "short" });
}

function trendPct(current, prev) {
    if (!prev) return current > 0 ? 100 : 0;
    return Math.round(((current - prev) / prev) * 100);
}

const SOURCE_META = {
    direct:    { label: "Direct",    icon: "🔗", color: TEAL },
    whatsapp:  { label: "WhatsApp",  icon: "💬", color: "#25d366" },
    linkedin:  { label: "LinkedIn",  icon: "💼", color: "#0a66c2" },
    instagram: { label: "Instagram", icon: "📸", color: "#e1306c" },
    twitter:   { label: "Twitter/X", icon: "𝕏", color: "#1da1f2" },
    facebook:  { label: "Facebook",  icon: "👤", color: "#1877f2" },
    telegram:  { label: "Telegram",  icon: "✈️", color: "#2aabee" },
    google:    { label: "Google",    icon: "🔍", color: AMBER },
    other:     { label: "Other",     icon: "🌐", color: "rgba(255,255,255,0.3)" },
};

function CustomTooltip({ active, payload, label }) {
    if (!active || !payload?.length) return null;
    return (
        <div style={{ background: "#0f1318", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, padding: "10px 14px", fontSize: 12 }}>
            <div style={{ color: "rgba(255,255,255,0.5)", marginBottom: 6, fontWeight: 600 }}>{label}</div>
            {payload.map(p => (
                <div key={p.dataKey} style={{ color: p.color, fontWeight: 700 }}>
                    {p.name}: {p.value}
                </div>
            ))}
        </div>
    );
}

function StatCard({ icon, label, value, color, trend, sub }) {
    const up = trend >= 0;
    return (
        <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 20, padding: "20px 18px", flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
                <div style={{ width: 38, height: 38, borderRadius: 11, background: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17 }}>{icon}</div>
                {trend !== undefined && (
                    <div style={{ background: up ? "rgba(10,173,161,0.12)" : "rgba(239,68,68,0.12)", color: up ? TEAL : "#ef4444", fontSize: 11, fontWeight: 700, borderRadius: 100, padding: "3px 8px" }}>
                        {up ? "↑" : "↓"} {Math.abs(trend)}%
                    </div>
                )}
            </div>
            <div style={{ fontSize: 30, fontWeight: 800, color: "#fff", lineHeight: 1, marginBottom: 4 }}>{fmt(value)}</div>
            <div style={{ fontSize: 12, fontWeight: 600, color, marginBottom: 2 }}>{label}</div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.25)" }}>{sub}</div>
        </div>
    );
}

export default function AnalyticsPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [cardData, setCardData]     = useState(null);
    const [analytics, setAnalytics]   = useState(null);
    const [loading, setLoading]       = useState(true);
    const [cardOwnerId, setCardOwnerId] = useState(null);
    const [range, setRange]           = useState(7);

    useEffect(() => {
        const load = async () => {
            try {
                const snap = await getDocs(query(collection(db, "cards"), where("slug", "==", id)));
                if (snap.empty) { setLoading(false); return; }
                const data = snap.docs[0].data();
                setCardData(data);
                setCardOwnerId(data.userId || null);
                const aSnap = await getDoc(doc(db, "analytics", id));
                setAnalytics(aSnap.exists() ? aSnap.data() : {});
            } catch (e) { console.error(e); }
            finally { setLoading(false); }
        };
        load();
    }, [id]);

    if (user === undefined || loading) {
        return (
            <div style={s.page}>
                <div style={{ color: "rgba(255,255,255,0.3)", fontSize: 14, margin: "auto" }}>Loading…</div>
            </div>
        );
    }

    if (!user) return <GateScreen icon="🔒" title="Sign in required" navigate={navigate} />;
    if (cardOwnerId && user.uid !== cardOwnerId) return <GateScreen icon="⛔" title="Access denied" navigate={navigate} />;

    const daily        = parseDaily(analytics || {});
    const sources      = parseSources(analytics || {});
    const totalViews   = analytics?.total_views  || 0;
    const totalSaves   = analytics?.total_saves  || 0;
    const totalWallet  = analytics?.total_wallet || 0;

    const days         = getDays(range);
    const prevDays     = getDays(range * 2).slice(0, range);

    const periodViews  = days.reduce((s, d) => s + (daily[d]?.views || 0), 0);
    const prevViews    = prevDays.reduce((s, d) => s + (daily[d]?.views || 0), 0);
    const trend        = trendPct(periodViews, prevViews);

    const today        = new Date().toISOString().split("T")[0];
    const todayViews   = daily[today]?.views || 0;
    const yesterday    = getDays(2)[0];
    const yestViews    = daily[yesterday]?.views || 0;
    const todayTrend   = trendPct(todayViews, yestViews);

    // Chart data
    const chartData = days.map(d => ({
        date: shortDate(d, range),
        Views: daily[d]?.views || 0,
        Saves: daily[d]?.saves || 0,
    }));

    // Peak day
    const peakEntry = chartData.reduce((a, b) => b.Views > a.Views ? b : a, { Views: 0, date: "—" });

    // Source breakdown
    const totalSrc = Object.values(sources).reduce((a, b) => a + b, 0);
    const topSources = Object.entries(sources)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5);

    // Daily table — last 14 days with data
    const tableRows = getDays(14)
        .filter(d => daily[d])
        .reverse()
        .map(d => ({ date: d, ...daily[d] }));

    return (
        <div style={s.page}>
            {/* Top bar */}
            <div style={s.topBar}>
                <button onClick={() => navigate(`/${id}`)} style={s.backBtn}>← Back to card</button>
                <span style={s.topLogo}>myprofilecorner</span>
                <button onClick={() => navigate(`/analytics/${id}/traffic`)} style={s.trafficBtn}>Traffic →</button>
            </div>

            <div style={s.body}>
                {/* Hero */}
                <div style={s.hero}>
                    {cardData?.photoUrl && (
                        <img src={cardData.photoUrl} alt="" style={s.avatar} />
                    )}
                    <div style={s.heroChip}>📊 Analytics</div>
                    <h1 style={s.heroName}>{cardData?.name || id}</h1>
                    {cardData?.designation && <p style={s.heroRole}>{cardData.designation}{cardData.company ? ` · ${cardData.company}` : ""}</p>}
                    <p style={s.heroSub}>myprofilecorner.com/{id}</p>
                </div>

                {/* Stat row */}
                <div style={s.statRow}>
                    <StatCard icon="👁" label="Total Views"  value={totalViews}  color={TEAL}   trend={trend}      sub={`+${periodViews} this period`} />
                    <StatCard icon="⚡" label="Today"        value={todayViews}  color={AMBER}  trend={todayTrend} sub="vs yesterday" />
                    <StatCard icon="💾" label="Saves"        value={totalSaves}  color={PURPLE} sub="vCard downloads" />
                    <StatCard icon="📲" label="Wallet Adds"  value={totalWallet} color={AMBER}  sub="Google Wallet" />
                </div>

                {/* Area chart */}
                <div style={s.card}>
                    <div style={s.cardHeader}>
                        <div>
                            <div style={s.cardTitle}>View History</div>
                            <div style={s.cardSub}>Peak: {peakEntry.Views} views on {peakEntry.date}</div>
                        </div>
                        <div style={s.rangePills}>
                            {[7, 14, 30].map(r => (
                                <button key={r} onClick={() => setRange(r)} style={{ ...s.rangePill, ...(range === r ? s.rangePillActive : {}) }}>
                                    {r}d
                                </button>
                            ))}
                        </div>
                    </div>
                    <ResponsiveContainer width="100%" height={200}>
                        <AreaChart data={chartData} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                            <defs>
                                <linearGradient id="gViews" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={TEAL} stopOpacity={0.3} />
                                    <stop offset="100%" stopColor={TEAL} stopOpacity={0} />
                                </linearGradient>
                                <linearGradient id="gSaves" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={PURPLE} stopOpacity={0.25} />
                                    <stop offset="100%" stopColor={PURPLE} stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                            <XAxis dataKey="date" tick={{ fill: "rgba(255,255,255,0.25)", fontSize: 10 }} axisLine={false} tickLine={false} interval={range === 7 ? 0 : range === 14 ? 1 : 4} />
                            <YAxis tick={{ fill: "rgba(255,255,255,0.2)", fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                            <Tooltip content={<CustomTooltip />} />
                            <Area type="monotone" dataKey="Views" stroke={TEAL} strokeWidth={2} fill="url(#gViews)" dot={false} activeDot={{ r: 5, fill: TEAL }} />
                            <Area type="monotone" dataKey="Saves" stroke={PURPLE} strokeWidth={1.5} fill="url(#gSaves)" dot={false} activeDot={{ r: 4, fill: PURPLE }} />
                        </AreaChart>
                    </ResponsiveContainer>
                    <div style={{ display: "flex", gap: 16, marginTop: 12, justifyContent: "center" }}>
                        {[{ color: TEAL, label: "Views" }, { color: PURPLE, label: "Saves" }].map(l => (
                            <div key={l.label} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "rgba(255,255,255,0.4)", fontWeight: 600 }}>
                                <div style={{ width: 8, height: 8, borderRadius: 2, background: l.color }} /> {l.label}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Traffic Sources */}
                {topSources.length > 0 && (
                    <div style={s.card}>
                        <div style={s.cardHeader}>
                            <div style={s.cardTitle}>Traffic Sources</div>
                            <button onClick={() => navigate(`/analytics/${id}/traffic`)} style={s.linkBtn}>Full breakdown →</button>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                            {topSources.map(([src, count]) => {
                                const meta = SOURCE_META[src] || { label: src, icon: "🌐", color: "rgba(255,255,255,0.3)" };
                                const pct = totalSrc ? Math.round((count / totalSrc) * 100) : 0;
                                return (
                                    <div key={src}>
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
                                            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "rgba(255,255,255,0.7)", fontWeight: 600 }}>
                                                <span>{meta.icon}</span> {meta.label}
                                            </div>
                                            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", fontWeight: 700 }}>{count} <span style={{ color: "rgba(255,255,255,0.2)" }}>({pct}%)</span></div>
                                        </div>
                                        <div style={{ height: 5, borderRadius: 3, background: "rgba(255,255,255,0.06)", overflow: "hidden" }}>
                                            <div style={{ height: "100%", width: `${pct}%`, background: meta.color, borderRadius: 3, transition: "width 0.6s cubic-bezier(0.22,1,0.36,1)" }} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Daily table */}
                {tableRows.length > 0 && (
                    <div style={s.card}>
                        <div style={{ ...s.cardHeader, marginBottom: 14 }}>
                            <div style={s.cardTitle}>Daily Breakdown</div>
                            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.2)", fontWeight: 600 }}>Last 14 days</div>
                        </div>
                        <div style={s.tableHead}>
                            <span>Date</span><span style={{ textAlign: "right" }}>Views</span><span style={{ textAlign: "right" }}>Saves</span><span style={{ textAlign: "right" }}>Wallet</span>
                        </div>
                        {tableRows.map((row, i) => {
                            const isToday = row.date === today;
                            return (
                                <div key={row.date} style={{ ...s.tableRow, background: isToday ? "rgba(10,173,161,0.05)" : "transparent", borderRadius: isToday ? 10 : 0 }}>
                                    <span style={{ color: isToday ? TEAL : "rgba(255,255,255,0.5)", fontWeight: isToday ? 700 : 500, fontSize: 13 }}>
                                        {isToday ? "Today" : new Date(row.date + "T00:00:00").toLocaleDateString("en", { day: "numeric", month: "short" })}
                                    </span>
                                    <span style={{ color: TEAL, fontWeight: 700, textAlign: "right" }}>{row.views || 0}</span>
                                    <span style={{ color: PURPLE, fontWeight: 700, textAlign: "right" }}>{row.saves || 0}</span>
                                    <span style={{ color: AMBER, fontWeight: 700, textAlign: "right" }}>{row.wallet || 0}</span>
                                </div>
                            );
                        })}
                    </div>
                )}

                {tableRows.length === 0 && (
                    <div style={s.empty}>
                        <div style={{ fontSize: "2.5rem", marginBottom: 12 }}>📊</div>
                        <p style={{ color: "rgba(255,255,255,0.35)", fontSize: 14 }}>No data yet — share your card to start seeing analytics.</p>
                    </div>
                )}

                <p style={s.footer}>Updates in real time · myprofilecorner.com</p>
            </div>
        </div>
    );
}

function GateScreen({ icon, title, navigate }) {
    return (
        <div style={{ minHeight: "100vh", background: "#080b10", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Outfit','Segoe UI',sans-serif" }}>
            <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 20, padding: "3rem 2rem", textAlign: "center", maxWidth: 360 }}>
                <div style={{ fontSize: "2.5rem", marginBottom: "1rem" }}>{icon}</div>
                <h2 style={{ fontSize: "1.3rem", fontWeight: 700, color: "#fff", marginBottom: "1.5rem" }}>{title}</h2>
                <button onClick={() => navigate("/")} style={{ padding: "0.75rem 2rem", background: TEAL, color: "#fff", border: "none", borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>Go Home</button>
            </div>
        </div>
    );
}

const s = {
    page: { minHeight: "100vh", background: "#080b10", display: "flex", flexDirection: "column", fontFamily: "'Outfit','Segoe UI',sans-serif" },
    topBar: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 24px", borderBottom: "1px solid rgba(255,255,255,0.05)", position: "sticky", top: 0, zIndex: 100, background: "rgba(8,11,16,0.94)", backdropFilter: "blur(16px)" },
    backBtn: { background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", color: "#ccc", borderRadius: 10, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
    topLogo: { fontSize: 13, fontWeight: 700, color: "rgba(255,255,255,0.25)", letterSpacing: "0.5px" },
    trafficBtn: { background: "rgba(10,173,161,0.1)", border: "1px solid rgba(10,173,161,0.2)", color: TEAL, borderRadius: 10, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },

    body: { maxWidth: 680, width: "100%", margin: "0 auto", padding: "0 20px 80px" },

    hero: { textAlign: "center", padding: "40px 0 28px", display: "flex", flexDirection: "column", alignItems: "center" },
    avatar: { width: 64, height: 64, borderRadius: "50%", objectFit: "cover", border: `3px solid ${TEAL}`, marginBottom: 16 },
    heroChip: { display: "inline-block", background: "rgba(10,173,161,0.12)", color: TEAL, fontSize: 11, fontWeight: 700, letterSpacing: "1.2px", textTransform: "uppercase", borderRadius: 100, padding: "5px 14px", marginBottom: 14 },
    heroName: { fontSize: 28, fontWeight: 800, color: "#fff", margin: "0 0 4px", letterSpacing: "-0.5px" },
    heroRole: { fontSize: 13, color: "rgba(255,255,255,0.4)", margin: "0 0 4px", fontWeight: 500 },
    heroSub: { fontSize: 12, color: "rgba(255,255,255,0.2)", margin: 0 },

    statRow: { display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 14, "@media(max-width:600px)": { gridTemplateColumns: "1fr 1fr" } },

    card: { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 20, padding: "22px 20px", marginBottom: 14 },
    cardHeader: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20 },
    cardTitle: { fontSize: 13, fontWeight: 700, color: "rgba(255,255,255,0.7)", letterSpacing: "0.3px" },
    cardSub: { fontSize: 11, color: "rgba(255,255,255,0.25)", marginTop: 3 },

    rangePills: { display: "flex", gap: 4 },
    rangePill: { background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.35)", borderRadius: 8, padding: "5px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },
    rangePillActive: { background: "rgba(10,173,161,0.15)", border: "1px solid rgba(10,173,161,0.3)", color: TEAL },

    linkBtn: { background: "none", border: "none", color: TEAL, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", padding: 0 },

    tableHead: { display: "grid", gridTemplateColumns: "1fr 60px 60px 60px", fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.2)", textTransform: "uppercase", letterSpacing: "1px", padding: "0 6px 10px", borderBottom: "1px solid rgba(255,255,255,0.05)", marginBottom: 4 },
    tableRow: { display: "grid", gridTemplateColumns: "1fr 60px 60px 60px", padding: "10px 6px", borderBottom: "1px solid rgba(255,255,255,0.03)", alignItems: "center" },

    empty: { textAlign: "center", padding: "40px 20px" },
    footer: { textAlign: "center", fontSize: 11, color: "rgba(255,255,255,0.12)", marginTop: 24 },
};
